/**
 * main.ts —— 应用入口
 *
 * boot 四步，⚠ 顺序不能换：
 *   1. 装事件委托与依赖注入（api ↔ router 之间的钩子）
 *   2. 探登录态（/auth/me，quiet401——未登录是正常结果）
 *   3. 已登录则拉齐守卫要用的元数据（周期 + 队伍），⚠ 必须在起路由【之前】
 *   4. 起路由，跑第一次
 *
 * 第 3 步放在起路由之前是关键：守卫是同步的，它假定 meta 已经就绪。
 * 顺序换了，刷新页面时守卫会拿着空的队伍列表，把合法的 #/teams/T01 判成非法弹回首页。
 */
import { setApiHooks } from './api/client.js';
import * as meta from './shared/meta.js';
import { auth } from './api/index.js';
import { installEvents, setActions } from './shared/events.js';
import { installSelects } from './shared/select.js';
import { installDates } from './shared/datepick.js';
import { installRouter, go, route, retryCurrent } from './router/index.js';
import { confirmModal } from './shared/common.js';
import { $ } from './shared/dom.js';
import { errText } from './api/client.js';

async function boot(): Promise<void> {
  /* ---- 1. 依赖注入：api 与 shared 都不许反向 import router ---- */
  setApiHooks({
    onUnauthorized: () => {
      // 会话过期（后端重启、Cookie 到期）。清干净再跳，否则守卫会以为还登着
      meta.clear();
      go('#/login');
    },
  });

  setActions({
    go,
    retry: retryCurrent,
    logout: () => {
      confirmModal({
        title: '退出登录',
        html: '退出后需要重新输入账号与密码，确认退出？',
        okText: '退出登录',
        onOk: () => {
          void auth
            .logout()
            .catch(() => {
              /* 服务端已经没有这个会话时也算退成功，不要卡住用户 */
            })
            .finally(() => {
              meta.clear();
              go('#/login');
            });
        },
      });
    },
  });

  installEvents();
  /* 下拉外观增强：装一次，之后新插入的 select 由内部的 MutationObserver 自动接管 */
  installSelects();
  installDates();

  /* ---- 2. 探登录态 ---- */
  const u = await meta.loadMe();

  /* ---- 3. 已登录就拉齐守卫要用的元数据 ----
     ⚠ 2026-08-26 去掉首登强制改密后，这里不再有"登录了却什么都拉不动"的中间态。 */
  if (u) {
    try {
      await meta.loadRefs();
    } catch (e) {
      return fatal(e);
    }
  }

  /* ---- 4. 起路由 ---- */
  installRouter();
  route();
}

/**
 * boot 失败的兜底屏。
 * ⚠ 这是【单机部署没有监控大盘】时唯一的自助排查入口：
 *   99% 的情况是后端没起或数据库连不上，直接把 /api/health 指给用户看，比一个白屏强得多。
 */
function fatal(e: unknown): void {
  document.body.classList.remove('anon');
  const main = $('#main');
  if (!main) return;
  main.innerHTML = `<div class="empty">
    <div class="et">系统暂时无法使用</div>
    <div class="ed">${errText(e)}</div>
    <p class="hint" style="margin-top:14px">
      多数情况是后端服务未启动或数据库连接失败。
      可打开 <a href="/api/health" target="_blank" rel="noopener">/api/health</a> 查看服务状态，
      或联系项目组管理员（处置步骤见《12_运维手册》）。
    </p>
  </div>`;
}

void boot();
